<div class="flight-page" [class.embedded]="embedded">
  @if (embedded) {
    <div class="card-actions">
      <button type="button" class="btn btn-primary btn-sm" (click)="showForm = !showForm">
        {{ showForm ? 'Close form' : 'New flight request' }}
      </button>
    </div>
  } @else {
    <div class="page-heading">
      <div>
        <h1>Flight Booking</h1>
        <p class="page-subtitle">
          Ask us to book your study-related travel. Provide the trip details and the flight
          options you would accept — a reviewer will approve or reject the request.
        </p>
      </div>
      <button type="button" class="btn btn-primary" (click)="showForm = !showForm">
        {{ showForm ? 'Close form' : 'New flight request' }}
      </button>
    </div>
  }

  @if (error) { <div class="alert alert-danger" role="alert">{{ error }}</div> }
  @if (message) { <div class="alert alert-success" role="status">{{ message }}</div> }

  @if (showForm) {
    <form class="card" [formGroup]="form" (ngSubmit)="submit()">
      <h2 class="card-title">New flight request</h2>
      <div class="card-body">
        <h3 class="form-section-title">Trip details</h3>
        <div class="form-grid">
          <div class="form-group">
            <label for="departureCity">Departure city <span class="required">*</span></label>
            <input id="departureCity" class="form-control" formControlName="departureCity" />
          </div>
          <div class="form-group">
            <label for="departureAirportCode">Departure airport code</label>
            <input id="departureAirportCode" class="form-control" formControlName="departureAirportCode" placeholder="e.g. JNB" />
          </div>
          <div class="form-group">
            <label for="destinationCity">Destination city <span class="required">*</span></label>
            <input id="destinationCity" class="form-control" formControlName="destinationCity" />
          </div>
          <div class="form-group">
            <label for="destinationAirportCode">Destination airport code</label>
            <input id="destinationAirportCode" class="form-control" formControlName="destinationAirportCode" placeholder="e.g. AMS" />
          </div>
          <div class="form-group">
            <label for="requestedDepartureDate">Departure date <span class="required">*</span></label>
            <input id="requestedDepartureDate" type="date" class="form-control" formControlName="requestedDepartureDate" />
          </div>
          <div class="form-group">
            <label for="requestedReturnDate">Return date</label>
            <input id="requestedReturnDate" type="date" class="form-control" formControlName="requestedReturnDate" />
          </div>
          <div class="form-group">
            <label for="preferredCabinClass">Preferred cabin</label>
            <select id="preferredCabinClass" class="form-control" formControlName="preferredCabinClass">
              <option value="Economy">Economy</option>
              <option value="Premium Economy">Premium Economy</option>
              <option value="Business">Business</option>
              <option value="First">First</option>
            </select>
          </div>
          <div class="form-group">
            <label for="purpose">Purpose of travel</label>
            <input id="purpose" class="form-control" formControlName="purpose" />
          </div>
          <div class="form-group full-width">
            <label class="checkbox-line">
              <input type="checkbox" formControlName="requiresPaidFlight" />
              <span>I expect to contribute towards the fare (paid flight)</span>
            </label>
          </div>
          <div class="form-group full-width">
            <label for="additionalNotes">Additional notes</label>
            <textarea id="additionalNotes" class="form-control" formControlName="additionalNotes"></textarea>
          </div>
        </div>

        <h3 class="form-section-title">Flight options you would accept</h3>
        <p class="hint">Add one or more itineraries. Mark the one you would prefer.</p>

        <div formArrayName="options">
          @for (option of options.controls; track $index; let i = $index) {
            <div class="option-row" [formGroupName]="i">
              <div class="form-group">
                <label>Airline <span class="required">*</span></label>
                <input class="form-control" formControlName="airline" />
              </div>
              <div class="form-group">
                <label>Flight number</label>
                <input class="form-control" formControlName="flightNumber" />
              </div>
              <div class="form-group">
                <label>Cabin</label>
                <input class="form-control" formControlName="cabinClass" />
              </div>
              <div class="form-group">
                <label>Estimated cost</label>
                <input type="number" class="form-control" formControlName="estimatedCost" />
              </div>
              <div class="form-group">
                <label>Currency</label>
                <input class="form-control" formControlName="currency" />
              </div>
              <div class="option-actions">
                <label class="checkbox-line">
                  <input type="radio" name="preferredOption" [checked]="option.get('isPreferred')?.value"
                    (change)="markPreferred(i)" />
                  <span>Preferred</span>
                </label>
                <button type="button" class="btn btn-link" (click)="removeOption(i)"
                  [disabled]="options.length === 1">Remove</button>
              </div>
            </div>
          }
        </div>

        <div class="form-actions">
          <button type="button" class="btn btn-secondary" (click)="addOption()">Add another option</button>
          <button type="submit" class="btn btn-primary" [disabled]="submitting">
            {{ submitting ? 'Submitting…' : 'Submit request' }}
          </button>
        </div>
      </div>
    </form>
  }

  <div class="card">
    <h2 class="card-title">Your requests</h2>
    <div class="card-body">
      <div class="table-responsive">
        <table class="table">
          <thead>
            <tr>
              <th>Route</th>
              <th>Status</th>
              <th>Departure date</th>
              <th>Submitted</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            @if (loading) {
              <tr>
                <td colspan="5" class="empty-cell"><i class="fas fa-spinner fa-spin"></i> Loading your flight
                requests…</td>
              </tr>
            }
            @if (!loading && requests.length === 0) {
              <tr>
                <td colspan="5" class="empty-cell">You have not requested a flight yet.</td>
              </tr>
            }
            @for (request of requests; track request.id) {
              <tr>
                <td>
                  <strong>
                    {{ request.departureCity }}@if (request.departureAirportCode) { ({{ request.departureAirportCode }}) }
                    → {{ request.destinationCity }}@if (request.destinationAirportCode) { ({{ request.destinationAirportCode }}) }
                  </strong>
                  <small>Reference: {{ request.referenceNumber }}</small>
                  <small>
                    {{ request.optionCount }} option(s){{ request.preferredCabinClass ? ' · ' + request.preferredCabinClass : '' }}{{ request.requiresPaidFlight ? ' · paid flight' : '' }}
                    @if (request.requestedReturnDate) { · Return {{ request.requestedReturnDate | date: 'dd MMM yyyy' }} }
                  </small>
                </td>
                <td><span [class]="statusClass(request.status)">{{ request.status }}</span></td>
                <td>{{ request.requestedDepartureDate | date: 'mediumDate' }}</td>
                <td>{{ request.createdDate | date: 'mediumDate' }}</td>
                <td>
                  @if (canWithdraw(request)) {
                    <button type="button" class="btn btn-secondary btn-sm" (click)="cancel(request)">Withdraw</button>
                  }
                </td>
              </tr>
              @if (request.status === 'REJECTED' && request.decisionReason) {
                <tr class="note-row">
                  <td colspan="5">
                    <div class="detail-note"><strong>Not approved.</strong> {{ request.decisionReason }}</div>
                  </td>
                </tr>
              }
              @if (request.bookingReference) {
                <tr class="note-row">
                  <td colspan="5">
                    <div class="detail-note"><strong>Booked.</strong> Your booking reference is
                    {{ request.bookingReference }}.</div>
                  </td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>
    </div>
  </div>
</div>
